import type { LocalizedCatalogues } from './translate';

/**
 * Shared game vocabulary, available to every game translator under `common.*`.
 * Games add their own keys in their own namespace (metadata.messages).
 */
export const COMMON_MESSAGES = {
  en: {
    'common.newGame': 'New game',
    'common.restart': 'Restart',
    'common.undo': 'Undo',
    'common.hint': 'Hint',
    'common.check': 'Check',
    'common.difficulty': 'Difficulty',
    'common.moves': 'Moves',
    'common.yourTurn': 'Your turn',
    'common.opponentTurn': 'Opponent’s turn',
    'common.thinking': 'Thinking…',
    'common.opponent': 'Opponent',
    'common.opponent.human': 'Another person (same device)',
    'common.opponent.computer': 'Computer',
    'common.won': 'You won.',
    'common.lost': 'You lost.',
    'common.draw': 'Draw.',
    'common.solved': 'Solved.',
    'common.player': 'Player {n}',
    'common.paused': 'Paused'
  },
  de: {
    'common.newGame': 'Neues Spiel',
    'common.restart': 'Neu starten',
    'common.undo': 'Rückgängig',
    'common.hint': 'Hinweis',
    'common.check': 'Prüfen',
    'common.difficulty': 'Schwierigkeit',
    'common.moves': 'Züge',
    'common.yourTurn': 'Du bist am Zug',
    'common.opponentTurn': 'Gegner ist am Zug',
    'common.thinking': 'Denkt nach …',
    'common.opponent': 'Gegner',
    'common.opponent.human': 'Andere Person (gleiches Gerät)',
    'common.opponent.computer': 'Computer',
    'common.won': 'Du hast gewonnen.',
    'common.lost': 'Du hast verloren.',
    'common.draw': 'Unentschieden.',
    'common.solved': 'Gelöst.',
    'common.player': 'Spieler {n}',
    'common.paused': 'Pausiert'
  },
  nl: {
    'common.newGame': 'Nieuw spel',
    'common.restart': 'Opnieuw beginnen',
    'common.undo': 'Ongedaan maken',
    'common.hint': 'Hint',
    'common.check': 'Controleren',
    'common.difficulty': 'Moeilijkheid',
    'common.moves': 'Zetten',
    'common.yourTurn': 'Jij bent aan de beurt',
    'common.opponentTurn': 'Tegenstander is aan de beurt',
    'common.thinking': 'Denkt na…',
    'common.opponent': 'Tegenstander',
    'common.opponent.human': 'Iemand anders (zelfde apparaat)',
    'common.opponent.computer': 'Computer',
    'common.won': 'Je hebt gewonnen.',
    'common.lost': 'Je hebt verloren.',
    'common.draw': 'Gelijkspel.',
    'common.solved': 'Opgelost.',
    'common.player': 'Speler {n}',
    'common.paused': 'Gepauzeerd'
  },
  es: {
    'common.newGame': 'Nueva partida',
    'common.restart': 'Reiniciar',
    'common.undo': 'Deshacer',
    'common.hint': 'Pista',
    'common.check': 'Comprobar',
    'common.difficulty': 'Dificultad',
    'common.moves': 'Movimientos',
    'common.yourTurn': 'Tu turno',
    'common.opponentTurn': 'Turno del rival',
    'common.thinking': 'Pensando…',
    'common.opponent': 'Rival',
    'common.opponent.human': 'Otra persona (mismo dispositivo)',
    'common.opponent.computer': 'Ordenador',
    'common.won': 'Has ganado.',
    'common.lost': 'Has perdido.',
    'common.draw': 'Empate.',
    'common.solved': 'Resuelto.',
    'common.player': 'Jugador {n}',
    'common.paused': 'En pausa'
  },
  fr: {
    'common.newGame': 'Nouvelle partie',
    'common.restart': 'Recommencer',
    'common.undo': 'Annuler',
    'common.hint': 'Indice',
    'common.check': 'Vérifier',
    'common.difficulty': 'Difficulté',
    'common.moves': 'Coups',
    'common.yourTurn': 'À vous de jouer',
    'common.opponentTurn': 'Au tour de l’adversaire',
    'common.thinking': 'Réflexion…',
    'common.opponent': 'Adversaire',
    'common.opponent.human': 'Une autre personne (même appareil)',
    'common.opponent.computer': 'Ordinateur',
    'common.won': 'Vous avez gagné.',
    'common.lost': 'Vous avez perdu.',
    'common.draw': 'Match nul.',
    'common.solved': 'Résolu.',
    'common.player': 'Joueur {n}',
    'common.paused': 'En pause'
  },
  ru: {
    'common.newGame': 'Новая игра',
    'common.restart': 'Начать заново',
    'common.undo': 'Отменить',
    'common.hint': 'Подсказка',
    'common.check': 'Проверить',
    'common.difficulty': 'Сложность',
    'common.moves': 'Ходы',
    'common.yourTurn': 'Ваш ход',
    'common.opponentTurn': 'Ход соперника',
    'common.thinking': 'Думает…',
    'common.opponent': 'Соперник',
    'common.opponent.human': 'Другой человек (на этом устройстве)',
    'common.opponent.computer': 'Компьютер',
    'common.won': 'Вы выиграли.',
    'common.lost': 'Вы проиграли.',
    'common.draw': 'Ничья.',
    'common.solved': 'Решено.',
    'common.player': 'Игрок {n}',
    'common.paused': 'Пауза'
  },
  'zh-Hans': {
    'common.newGame': '新游戏',
    'common.restart': '重新开始',
    'common.undo': '撤销',
    'common.hint': '提示',
    'common.check': '检查',
    'common.difficulty': '难度',
    'common.moves': '步数',
    'common.yourTurn': '轮到你了',
    'common.opponentTurn': '轮到对手',
    'common.thinking': '思考中…',
    'common.opponent': '对手',
    'common.opponent.human': '另一个人（同一设备）',
    'common.opponent.computer': '电脑',
    'common.won': '你赢了。',
    'common.lost': '你输了。',
    'common.draw': '平局。',
    'common.solved': '已解出。',
    'common.player': '玩家 {n}',
    'common.paused': '已暂停'
  },
  ko: {
    'common.newGame': '새 게임',
    'common.restart': '다시 시작',
    'common.undo': '되돌리기',
    'common.hint': '힌트',
    'common.check': '확인',
    'common.difficulty': '난이도',
    'common.moves': '이동 수',
    'common.yourTurn': '내 차례예요',
    'common.opponentTurn': '상대 차례예요',
    'common.thinking': '생각 중…',
    'common.opponent': '상대',
    'common.opponent.human': '다른 사람 (같은 기기)',
    'common.opponent.computer': '컴퓨터',
    'common.won': '이겼어요.',
    'common.lost': '졌어요.',
    'common.draw': '비겼어요.',
    'common.solved': '풀었어요.',
    'common.player': '플레이어 {n}',
    'common.paused': '일시 정지됨'
  },
  ja: {
    'common.newGame': '新しいゲーム',
    'common.restart': 'やり直す',
    'common.undo': '元に戻す',
    'common.hint': 'ヒント',
    'common.check': 'チェック',
    'common.difficulty': '難易度',
    'common.moves': '手数',
    'common.yourTurn': 'あなたの番です',
    'common.opponentTurn': '相手の番です',
    'common.thinking': '考え中…',
    'common.opponent': '対戦相手',
    'common.opponent.human': 'ほかの人（同じデバイス）',
    'common.opponent.computer': 'コンピューター',
    'common.won': 'あなたの勝ちです。',
    'common.lost': 'あなたの負けです。',
    'common.draw': '引き分けです。',
    'common.solved': '解けました。',
    'common.player': 'プレイヤー {n}',
    'common.paused': '一時停止中'
  },
  ar: {
    'common.newGame': 'لعبة جديدة',
    'common.restart': 'إعادة البدء',
    'common.undo': 'تراجع',
    'common.hint': 'تلميح',
    'common.check': 'تحقّق',
    'common.difficulty': 'الصعوبة',
    'common.moves': 'النقلات',
    'common.yourTurn': 'دورك',
    'common.opponentTurn': 'دور الخصم',
    'common.thinking': 'يفكّر…',
    'common.opponent': 'الخصم',
    'common.opponent.human': 'شخص آخر (على الجهاز نفسه)',
    'common.opponent.computer': 'الحاسوب',
    'common.won': 'لقد فزت.',
    'common.lost': 'لقد خسرت.',
    'common.draw': 'تعادل.',
    'common.solved': 'تم الحل.',
    'common.player': 'اللاعب {n}',
    'common.paused': 'متوقف مؤقتًا'
  },
  pt: {
    'common.newGame': 'Novo jogo',
    'common.restart': 'Reiniciar',
    'common.undo': 'Desfazer',
    'common.hint': 'Dica',
    'common.check': 'Verificar',
    'common.difficulty': 'Dificuldade',
    'common.moves': 'Jogadas',
    'common.yourTurn': 'É a sua vez',
    'common.opponentTurn': 'Vez do adversário',
    'common.thinking': 'Pensando…',
    'common.opponent': 'Adversário',
    'common.opponent.human': 'Outra pessoa (mesmo dispositivo)',
    'common.opponent.computer': 'Computador',
    'common.won': 'Venceu.',
    'common.lost': 'Perdeu.',
    'common.draw': 'Empate.',
    'common.solved': 'Resolvido.',
    'common.player': 'Jogador {n}',
    'common.paused': 'Em pausa'
  },
  it: {
    'common.newGame': 'Nuova partita',
    'common.restart': 'Ricomincia',
    'common.undo': 'Annulla',
    'common.hint': 'Suggerimento',
    'common.check': 'Verifica',
    'common.difficulty': 'Difficoltà',
    'common.moves': 'Mosse',
    'common.yourTurn': 'Tocca a te',
    'common.opponentTurn': 'Tocca all’avversario',
    'common.thinking': 'Sta pensando…',
    'common.opponent': 'Avversario',
    'common.opponent.human': 'Un’altra persona (stesso dispositivo)',
    'common.opponent.computer': 'Computer',
    'common.won': 'Hai vinto.',
    'common.lost': 'Hai perso.',
    'common.draw': 'Pareggio.',
    'common.solved': 'Risolto.',
    'common.player': 'Giocatore {n}',
    'common.paused': 'In pausa'
  },
  pl: {
    'common.newGame': 'Nowa gra',
    'common.restart': 'Zacznij od nowa',
    'common.undo': 'Cofnij',
    'common.hint': 'Podpowiedź',
    'common.check': 'Sprawdź',
    'common.difficulty': 'Poziom trudności',
    'common.moves': 'Ruchy',
    'common.yourTurn': 'Twój ruch',
    'common.opponentTurn': 'Ruch przeciwnika',
    'common.thinking': 'Myśli…',
    'common.opponent': 'Przeciwnik',
    'common.opponent.human': 'Inna osoba (to samo urządzenie)',
    'common.opponent.computer': 'Komputer',
    'common.won': 'Wygrana.',
    'common.lost': 'Przegrana.',
    'common.draw': 'Remis.',
    'common.solved': 'Rozwiązane.',
    'common.player': 'Gracz {n}',
    'common.paused': 'Wstrzymano'
  },
  tr: {
    'common.newGame': 'Yeni oyun',
    'common.restart': 'Yeniden başlat',
    'common.undo': 'Geri al',
    'common.hint': 'İpucu',
    'common.check': 'Kontrol et',
    'common.difficulty': 'Zorluk',
    'common.moves': 'Hamleler',
    'common.yourTurn': 'Sıra sende',
    'common.opponentTurn': 'Sıra rakipte',
    'common.thinking': 'Düşünüyor…',
    'common.opponent': 'Rakip',
    'common.opponent.human': 'Başka biri (aynı cihaz)',
    'common.opponent.computer': 'Bilgisayar',
    'common.won': 'Kazandın.',
    'common.lost': 'Kaybettin.',
    'common.draw': 'Berabere.',
    'common.solved': 'Çözüldü.',
    'common.player': 'Oyuncu {n}',
    'common.paused': 'Duraklatıldı'
  },
  uk: {
    'common.newGame': 'Нова гра',
    'common.restart': 'Почати знову',
    'common.undo': 'Скасувати',
    'common.hint': 'Підказка',
    'common.check': 'Перевірити',
    'common.difficulty': 'Складність',
    'common.moves': 'Ходи',
    'common.yourTurn': 'Ваш хід',
    'common.opponentTurn': 'Хід суперника',
    'common.thinking': 'Думає…',
    'common.opponent': 'Суперник',
    'common.opponent.human': 'Інша людина (на цьому пристрої)',
    'common.opponent.computer': 'Комп’ютер',
    'common.won': 'Ви виграли.',
    'common.lost': 'Ви програли.',
    'common.draw': 'Нічия.',
    'common.solved': 'Розв’язано.',
    'common.player': 'Гравець {n}',
    'common.paused': 'Пауза'
  },
  hi: {
    'common.newGame': 'नया खेल',
    'common.restart': 'फिर से शुरू करें',
    'common.undo': 'पूर्ववत करें',
    'common.hint': 'संकेत',
    'common.check': 'जाँचें',
    'common.difficulty': 'कठिनाई',
    'common.moves': 'चालें',
    'common.yourTurn': 'आपकी बारी',
    'common.opponentTurn': 'विरोधी की बारी',
    'common.thinking': 'सोच रहा है…',
    'common.opponent': 'विरोधी',
    'common.opponent.human': 'कोई और व्यक्ति (इसी डिवाइस पर)',
    'common.opponent.computer': 'कंप्यूटर',
    'common.won': 'आप जीत गए।',
    'common.lost': 'आप हार गए।',
    'common.draw': 'मुक़ाबला बराबर रहा।',
    'common.solved': 'हल हो गया।',
    'common.player': 'खिलाड़ी {n}',
    'common.paused': 'रुका हुआ'
  }
} as const satisfies LocalizedCatalogues;
